import { INVOICE_LIST_MAX, type InvoiceSummary } from '@morales/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { QueryView } from '../components/ui';
import { addDays, formatDate, formatMoney, todayIso } from '../format';
import { useLocale } from '../i18n';

export type InvoiceState = 'open' | 'overdue' | 'paid' | 'void';
const STATES = ['all', 'open', 'overdue', 'paid', 'void'] as const;
type Filter = (typeof STATES)[number];

/** Where an invoice stands today: void, paid (nothing owed), overdue (owed past its due date) or open. */
export function stateOf(i: InvoiceSummary, today: string): InvoiceState | null {
  if (i.status === 'void') return 'void';
  if (i.balance === null) return null; // the books are not started for this invoice: no claim either way
  if (i.balance <= 0) return 'paid';
  return (i.dueDate ?? i.invoiceDate) < today ? 'overdue' : 'open';
}

type SortKey = 'no' | 'date' | 'customer' | 'due' | 'total' | 'balance';
const sorters: Record<SortKey, (a: InvoiceSummary, b: InvoiceSummary) => number> = {
  no: (a, b) => a.invoiceNo - b.invoiceNo,
  date: (a, b) => a.invoiceDate.localeCompare(b.invoiceDate) || a.invoiceNo - b.invoiceNo,
  customer: (a, b) => a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' }),
  due: (a, b) => (a.dueDate ?? a.invoiceDate).localeCompare(b.dueDate ?? b.invoiceDate),
  total: (a, b) => a.total - b.total,
  balance: (a, b) => (a.balance ?? 0) - (b.balance ?? 0),
};

/** QuickBooks' invoice list on the sheet: dates, status, search, totals, and Enter opens the invoice. */
export function InvoicesPage() {
  const { t, intlLocale } = useLocale();
  const navigate = useNavigate();
  const today = todayIso();
  const [from, setFrom] = useState(addDays(today, -30));
  const [to, setTo] = useState(today);
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'date', dir: -1 });
  const list = useQuery({ queryKey: ['invoices', 'list', from, to], queryFn: ({ signal }) => api.invoices({ from, to }, signal) });

  const words = q.trim().toLowerCase();
  const rows = useMemo(() => {
    const all = (list.data ?? []).filter((i) => {
      if (filter !== 'all' && stateOf(i, today) !== filter) return false;
      return !words || i.customerName.toLowerCase().includes(words) || String(i.invoiceNo).includes(words);
    });
    return [...all].sort((a, b) => sorters[sort.key](a, b) * sort.dir);
  }, [list.data, filter, words, sort, today]);
  // a test invoice (decision 140) is listed, marked, but counts in no total
  const live = rows.filter((i) => i.status !== 'void' && !i.test);
  const sum = (f: (i: InvoiceSummary) => number) => Math.round(live.reduce((s, i) => s + f(i), 0) * 100) / 100;
  const billed = sum((i) => i.total);
  // an invoice paid past its total holds money on account, not a negative balance owed
  const open = sum((i) => Math.max(0, i.balance ?? 0));
  const overdue = sum((i) => (stateOf(i, today) === 'overdue' ? (i.balance ?? 0) : 0));

  const head = (key: SortKey, label: string) => (
    <button type="button" className="th-sort" onClick={() => setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: key === 'date' || key === 'total' || key === 'balance' ? -1 : 1 }))} aria-label={`${t('sortBy')}: ${label}`}>
      {label}
      <span className="sort-mark" aria-hidden="true">{sort.key === key ? (sort.dir === 1 ? '▲' : '▼') : ''}</span>
    </button>
  );
  const stateLabel: Record<InvoiceState, string> = { open: t('stateOpen'), overdue: t('stateOverdue'), paid: t('statePaid'), void: t('stateVoid') };
  const columns: SheetColumn<InvoiceSummary>[] = [
    { key: 'no', header: head('no', t('colNo')), width: '84px', align: 'right', render: (i) => <Link to={`/invoices/${i.id}`}>{i.invoiceNo}</Link> },
    { key: 'date', header: head('date', t('invoiceDate')), width: '120px', render: (i) => formatDate(i.invoiceDate, intlLocale, 'date') },
    { key: 'customer', header: head('customer', t('customer')), width: '28%', render: (i) => i.customerName },
    { key: 'due', header: head('due', t('dueDate')), width: '120px', render: (i) => (i.dueDate ? formatDate(i.dueDate, intlLocale, 'date') : '') },
    {
      key: 'state',
      header: t('colStatus'),
      width: '104px',
      render: (i) => {
        const s = stateOf(i, today);
        return s ? (
          <>
            {i.test ? <span className="tag tag-test">{t('testTag')}</span> : null}
            <span className={`state state-${s}`}>{stateLabel[s]}</span>
          </>
        ) : '';
      },
    },
    { key: 'total', header: head('total', t('total')), width: '112px', align: 'right', render: (i) => formatMoney(i.total, intlLocale), cellClass: (i) => (i.status === 'void' ? 'struck' : undefined) },
    { key: 'paid', header: t('colPaid'), width: '112px', align: 'right', render: (i) => (i.paid === null ? '' : formatMoney(i.paid, intlLocale)) },
    { key: 'balance', header: head('balance', t('colBalance')), width: '112px', align: 'right', render: (i) => (i.balance === null || i.status === 'void' ? '' : i.balance < -0.004 ? <span className="muted">{t('onAccountAmount').replace('{amount}', formatMoney(-i.balance, intlLocale))}</span> : <strong>{formatMoney(i.balance, intlLocale)}</strong>), cellClass: (i) => (stateOf(i, today) === 'overdue' ? 'bad' : undefined) },
    { key: 'order', header: t('order'), width: '84px', align: 'right', render: (i) => (i.orderNo ? `#${i.orderNo}` : '') },
  ];

  return (
    <div className="desk-page">
      <header className="desk-head">
        <div>
          <h1>{t('invoicesTitle')}</h1>
          <p className="muted">{t('invoicesHint')}</p>
        </div>
        <Link to="/invoices/new" className="btn btn-primary btn-sm">
          {t('newInvoice')}
        </Link>
      </header>
      <div className="toolbar">
        <label className="tool">
          <span>{t('fromDate')}</span>
          <input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} />
        </label>
        <label className="tool">
          <span>{t('toDate')}</span>
          <input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} />
        </label>
        <div className="seg" role="group" aria-label={t('colStatus')}>
          {STATES.map((s) => (
            <button key={s} type="button" aria-pressed={filter === s} onClick={() => setFilter(s)}>
              {s === 'all' ? t('stateAll') : stateLabel[s]}
            </button>
          ))}
        </div>
        <input className="tool-search" type="search" placeholder={t('findInvoice')} aria-label={t('findInvoice')} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <dl className="totals-strip" data-testid="invoice-totals">
        <div>
          <dt>{t('invoicesCountCol')}</dt>
          <dd>{live.length}</dd>
        </div>
        <div>
          <dt>{t('billed')}</dt>
          <dd>{formatMoney(billed, intlLocale)}</dd>
        </div>
        <div>
          <dt>{t('stateOpen')}</dt>
          <dd>{formatMoney(open, intlLocale)}</dd>
        </div>
        <div className={overdue > 0 ? 'bad' : undefined}>
          <dt>{t('stateOverdue')}</dt>
          <dd>{formatMoney(overdue, intlLocale)}</dd>
        </div>
      </dl>
      <QueryView query={list}>
        {() =>
          rows.length === 0 ? (
            <p className="muted pick-first">{t('noInvoicesHere')}</p>
          ) : (
            <>
              {(list.data?.length ?? 0) >= INVOICE_LIST_MAX ? (
                <p className="note" role="status">
                  {t('invoicesCapped').replace('{n}', INVOICE_LIST_MAX.toLocaleString(intlLocale))}
                </p>
              ) : null}
              <Sheet label={t('invoicesTitle')} columns={columns} rows={rows} rowKey={(i) => i.id} minWidth="980px" onOpen={(i) => navigate(`/invoices/${i.id}`)} rowClass={(i) => (i.status === 'void' ? 'zero' : undefined)} />
            </>
          )
        }
      </QueryView>
    </div>
  );
}
